import { createContext, useContext, useEffect } from 'react'

/**
 * Lets a page hide the header's application action for one of its own states.
 *
 * Same shape as `stepperVisibility`, and it exists for the same reason: the
 * pathname cannot tell the layout which of the two frames on
 * `/apply/:appRefNo/submitted` is on screen. While §4.10 is in flight the page
 * is "Submitting Application"; once it lands the page is the receipt.
 *
 * The header's action must NOT be reachable during the first of those. Both
 * things it can offer are destructive and neither is safe mid-submission:
 * "Reset Application" would drop the session out from under a call whose result
 * is the only copy of the payment breakdown (§4.10 is non-idempotent, so there
 * is no second chance to read it), and "Cancel Application" would fire §8.1 at
 * an application the gateway is still finalising. The citizen cannot take back
 * a submission that is already on the wire, so the honest thing is to offer
 * nothing until it has landed.
 *
 * A separate context from the stepper's rather than one shared "the interstitial
 * is showing" flag, because the two pieces of chrome are owned by two different
 * layouts — the stepper by SidebarLayout, the header by PublicLayout above it —
 * and each keeps the state for its own.
 */
const HeaderActionSuppressionContext = createContext<(suppressed: boolean) => void>(() => {})

export const HeaderActionSuppressionProvider = HeaderActionSuppressionContext.Provider

/**
 * Hides the header action while `suppressed` is true, and restores it when the
 * page unmounts — so a screen that forgets to reset cannot strand the next one
 * without its action.
 */
export function useSuppressHeaderAction(suppressed: boolean): void {
  const setSuppressed = useContext(HeaderActionSuppressionContext)

  useEffect(() => {
    setSuppressed(suppressed)
    return () => {
      setSuppressed(false)
    }
  }, [setSuppressed, suppressed])
}
